import { createSlice,createSelector } from "@reduxjs/toolkit";
import { apiCallBegan } from "./api";
import config from "../../config.json";
import { createRequestState, requestFailed, requestStarted, requestSucceeded } from "./requestState";

const slice = createSlice({
    name: "workflowRoles",
    initialState: {
        list: [],
        loading: false,
        lastFetch: null,
        roleTypes:[],
        performers:[],
        filterList:[],
        allRolesPerformers:[],
        allRoles:[],
        requests: {
            list: createRequestState(),
            allRoles: createRequestState(),
            allRolesPerformers: createRequestState(),
            filter: createRequestState(),
            roleTypes: createRequestState(),
            performers: createRequestState(),
        }
    },
    reducers: {
        workflowRoleRequestStarted: (workflowRoles, action) => {
            requestStarted(workflowRoles.requests, action.payload);
            workflowRoles.loading = true;
        },
        workflowRoleRequestFailed: (workflowRoles, action) => {
            requestFailed(workflowRoles.requests, action.payload?.key, action.payload?.error || action.payload);
            workflowRoles.loading = false;
        },
        roleAdded: (workflowRoles, action) => {
            workflowRoles.list.push(action.payload);
        },
        allRolesReceived: (workflowRoles, action) => {
            workflowRoles.allRoles = action.payload;
            requestSucceeded(workflowRoles.requests, "allRoles");
        },
        rolesReceived: (workflowRoles, action) => {
            workflowRoles.list = action.payload;
            workflowRoles.loading = false;
            requestSucceeded(workflowRoles.requests, "list");
        },
        filterRolesReceived: (workflowRoles, action) => {
            workflowRoles.filterList = action.payload;
            workflowRoles.loading = false;
            requestSucceeded(workflowRoles.requests, "filter");
        },
        roleTypesReceived: (workflowRoles, action) => {
            workflowRoles.roleTypes = action.payload;
            workflowRoles.loading = false;
            requestSucceeded(workflowRoles.requests, "roleTypes");
        },
        rolePerformersReceived: (workflowRoles, action) => {
            workflowRoles.performers = action.payload;
            workflowRoles.loading = false;
            requestSucceeded(workflowRoles.requests, "performers");
        },
        allRolesPerformersReceived: (workflowRoles, action) => {
            workflowRoles.allRolesPerformers = action.payload;
            requestSucceeded(workflowRoles.requests, "allRolesPerformers");
        },
        loadingRequested: (workflowRoles) => {
            workflowRoles.loading = true;
        },
        loadingFailed: (workflowRoles) => {
            workflowRoles.loading = false;
        },
    }
});

export const { workflowRoleRequestStarted, workflowRoleRequestFailed,
    roleAdded, allRolesReceived, rolesReceived, roleTypesReceived,
    rolePerformersReceived, filterRolesReceived, allRolesPerformersReceived,
    loadingRequested, loadingFailed } = slice.actions;

export default slice.reducer;

export const loadWorkflowRoles = (pageSize, currentPage) => apiCallBegan({
    url: config.SetupUsers.GetWorkflowRoles + pageSize + "&currentPage=" + currentPage,
    onStart: { type: workflowRoleRequestStarted.type, payload: "list" },
    onSuccess: rolesReceived.type,
    onError: { type: workflowRoleRequestFailed.type, payload: { key: "list" } },
    cache: true,
    cacheDuration: 60000
});

export const loadAllWorkflowRoles = () => apiCallBegan({
    url: config.SetupUsers.GetAllWorkflowRoles,
    onStart: { type: workflowRoleRequestStarted.type, payload: "allRoles" },
    onSuccess: allRolesReceived.type,
    onError: { type: workflowRoleRequestFailed.type, payload: { key: "allRoles" } },
    cache: true,
    cacheDuration: 300000
});

export const loadAllWorkflowRolesPerformers = () => apiCallBegan({
    url: config.GetAllWorkflowPerformers,
    onStart: { type: workflowRoleRequestStarted.type, payload: "allRolesPerformers" },
    onSuccess: allRolesPerformersReceived.type,
    onError: { type: workflowRoleRequestFailed.type, payload: { key: "allRolesPerformers" } },
    cache: true,
    cacheDuration: 60000
});

export const loadWorkflowRolesByFilter = (data) => apiCallBegan({
    url: config.SetupUsers.GetWorkflowRolesByFilter,
    method: "post",
    data: data,
    onStart: { type: workflowRoleRequestStarted.type, payload: "filter" },
    onSuccess: filterRolesReceived.type,
    onError: { type: workflowRoleRequestFailed.type, payload: { key: "filter" } },
    cancelPrevious: true,
    cancelKey: "workflowRoles/filter"
});

export const loadRoleTypes = (name) => apiCallBegan({
    url: `${config.GetCodemanager}${name}`,
    onStart: { type: workflowRoleRequestStarted.type, payload: "roleTypes" },
    onSuccess: roleTypesReceived.type,
    onError: { type: workflowRoleRequestFailed.type, payload: { key: "roleTypes" } },
    cache: true,
    cacheDuration: 300000
});

export const loadRolePerformers = (id) => apiCallBegan({
    url: `${config.SetupUsers.GetWorkflowPerformers}${id}`,
    onStart: { type: workflowRoleRequestStarted.type, payload: "performers" },
    onSuccess: rolePerformersReceived.type,
    onError: { type: workflowRoleRequestFailed.type, payload: { key: "performers" } },
    cache: true,
    cacheDuration: 60000
});

export const getAllRoles= createSelector(
    state => state.entities.workflowRoles,
    workflowRoles => workflowRoles.list
);

export const getAllWorkfllowRoles= createSelector(
    state => state.entities.workflowRoles,
    workflowRoles => workflowRoles.allRoles
);

export const getFilterAllRoles= createSelector(
    state => state.entities.workflowRoles,
    workflowRoles => workflowRoles.filterList
);

export const getRoleTypes = createSelector(
    state => state.entities.workflowRoles,
    workflowRoles => workflowRoles.roleTypes
);

export const getRolePerformers = createSelector(
    state => state.entities.workflowRoles,
    workflowRoles => workflowRoles.performers
);

export const getAllRolesPerformers = createSelector(
    state => state.entities.workflowRoles,
    workflowRoles => workflowRoles.allRolesPerformers
);

export const getWorkflowRolesLoading = createSelector(
    state => state.entities.workflowRoles,
    workflowRoles => workflowRoles.loading
);

export const getWorkflowRoleRequests = createSelector(
    state => state.entities.workflowRoles,
    workflowRoles => workflowRoles.requests
);
